import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { Box } from '@mantine/core';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { ArrowGlyph, type ArrowSize, arrowSurfaceProps } from '~ui';

/**
 * The router-linked role of the arrow affordance (Figma G8). The visual — and the button and
 * decorative-mark roles — live in `internal/ui` (`ArrowButton`, `ArrowMark`), so fragments draw the
 * same circle; only the typed router link is a portal concern.
 */

type ArrowHostProps = {
  label: string;
  size?: ArrowSize;
} & Omit<ComponentPropsWithoutRef<'a'>, 'children' | 'aria-label'>;

const ArrowHost = forwardRef<HTMLAnchorElement, ArrowHostProps>(
  ({ label, size = 'md', ...rest }, ref) => (
    <Box component="a" ref={ref} aria-label={label} {...rest} {...arrowSurfaceProps(size)}>
      <ArrowGlyph size={size} />
    </Box>
  ),
);
ArrowHost.displayName = 'ArrowHost';
const CreatedArrowLink = createLink(ArrowHost);

/** @desc The arrow as a typed router link: `<ArrowButtonLink label="Open Entain" to="/operators/$slug" params={{ slug }} />`. */
export const ArrowButtonLink: LinkComponent<typeof ArrowHost> = (props) => (
  <CreatedArrowLink {...props} />
);
